import React, { useState, useEffect, useMemo } from 'react';
import { useSiteVarsFunctions } from "../../hooks/use-site-vars-functions";

export const PlayerImage = ({ player, style, alt, className }) => {
  const { getTeamById, getPlayerPositionById } = useSiteVarsFunctions();

  // Extract clean base photo id (e.g., "98980.jpg" -> "98980", "p98980.png" -> "98980")
  const photo = player?.photo || '';
  const cleanCode = photo 
    ? photo.replace('.jpg', '').replace('.png', '').replace(/^p/, '') 
    : (player?.code ? String(player.code) : '');

  const teamId = player?.teamId;
  const positionId = player?.positionId;
  const team = teamId ? getTeamById(teamId) : null;
  const position = positionId ? getPlayerPositionById(positionId) : null;
  const shirtUrl = (team && team.kitId && position && position.name_short)
    ? `https://fantasy.premierleague.com/dist/img/shirts/standard/shirt_${team.kitId}${position.name_short === "GKP" ? "_1" : ""}-66.webp`
    : null;

  // Build candidate URL fallback chain
  const candidateUrls = useMemo(() => {
    const list = [];
    if (cleanCode) {
      // 1. Premier League 2024/25 CDN path (e.g. 154561.png)
      list.push(`https://resources.premierleague.com/premierleague25/photos/players/110x140/${cleanCode}.png`);
      // 2. Standard Premier League CDN path with 'p' prefix (e.g. p98980.png)
      list.push(`https://resources.premierleague.com/premierleague/photos/players/110x140/p${cleanCode}.png`);
      // 3. Higher resolution standard Premier League CDN path
      list.push(`https://resources.premierleague.com/premierleague/photos/players/250x250/p${cleanCode}.png`);
    }
    // 4. Team official kit shirt as graceful fallback
    if (shirtUrl) {
      list.push(shirtUrl);
    }
    return list;
  }, [cleanCode, shirtUrl]);

  const [srcIndex, setSrcIndex] = useState(0);

  // Reset srcIndex if player/photo changes
  useEffect(() => {
    setSrcIndex(0);
  }, [cleanCode, shirtUrl]);

  const handleError = () => {
    setSrcIndex((prevIndex) => {
      if (prevIndex + 1 < candidateUrls.length) {
        return prevIndex + 1;
      }
      return prevIndex;
    });
  };

  const currentSrc = candidateUrls[srcIndex] || shirtUrl;
  const playerName = `${player?.firstName || ''} ${player?.lastName || ''}`.trim() || alt || 'Player';

  if (!currentSrc) {
    return (
      <div 
        style={{ 
          width: style?.width || '40px', 
          height: style?.height || '51px', 
          backgroundColor: '#e0e0e0', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          borderRadius: 4, 
          fontSize: '10px', 
          color: '#888',
          ...style 
        }}
      >
        {player?.lastName ? player.lastName.slice(0, 3).toUpperCase() : ''}
      </div>
    );
  }

  return (
    <img
      src={currentSrc}
      alt={playerName}
      onError={handleError}
      className={className}
      style={{
        width: '40px',
        height: '51px',
        objectFit: currentSrc.includes('shirt_') ? 'contain' : 'cover',
        ...style
      }}
    />
  );
};
